import { useCallback, useEffect, useMemo, useState } from "react";
import { pickerPage } from "../../lib/paging";
import { MarkPaidDialog } from "./MarkPaidDialog";
import { Link, useNavigate, useParams } from "react-router";
import { Button, Card, Cluster, Input, LabelWithHint, Select, Spinner, Stack, useToast } from "@salesapp/ui";
import { crmApi, type Order, type Product, type Quote, type QuoteLineItem, type QuoteVersionSummary, type RecordChange } from "../../lib/crmApi";
import { FIELD_HINTS, INVOICE_STATUS_OPTIONS, ORDER_STATUS_OPTIONS, QUOTE_STATUS_OPTIONS, ACTION_HINTS } from "../../help/fieldHints";
import { opsApi, type FxRate, type Invoice } from "../../lib/opsApi";
import { printDocumentHtml } from "../../lib/printDocument";
import { reportSaveFailure } from "../../lib/saveNotice";
import { DetailSection, FieldItem, RecordWorkspace, DeleteRecordButton } from "./RecordWorkspace";
import { ResponsiveActions } from "./ResponsiveActions";
import { StatusBadge } from "./StatusBadge";
import { RecordOwner } from "./RecordOwner";
import { ChangeHistoryPanel } from "./ChangeHistoryPanel";
import { QuoteVersionsPanel } from "./QuoteVersionsPanel";
import { useCrmHref } from "../../lib/customerUrl";
import { CURRENCY_SYMBOLS, quoteWorkflow, todayInZone } from "../../lib/localeFormat";
import { useLocale } from "../../shell/LocaleProvider";
import { useAccess } from "../../shell/AccessProvider";
import { useLoxleyOffer } from "../../loxley/LoxleyProvider";

type Kind = "quote" | "order" | "invoice";
type Tab = "document" | "edit" | "history";

function num(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

const CURRENCY_CODES = Object.keys(CURRENCY_SYMBOLS);

function emptyLine(): QuoteLineItem {
  return { description: "", quantity: 1, unit_price: 0, discount_pct: 0, currency: "", fx_rate: null, product_id: null, sort_order: 0 };
}

function rateLabel(line: QuoteLineItem, documentCurrency: string): string {
  const lineCurrency = (line.currency || documentCurrency).toUpperCase();
  const document = documentCurrency.toUpperCase();
  if (lineCurrency && document && lineCurrency !== document && line.fx_rate == null) {
    return "—";
  }
  return line.fx_rate == null ? "1" : String(line.fx_rate);
}

function lineNet(line: QuoteLineItem): number {
  const raw = num(line.quantity) * num(line.unit_price);
  return raw - raw * (num(line.discount_pct) / 100);
}

function convertedNet(line: QuoteLineItem, documentCurrency: string, rates: FxRate[] = []): number | null {
  const native = lineNet(line);
  const lineCurrency = (line.currency || documentCurrency).toUpperCase();
  if (!lineCurrency || lineCurrency === documentCurrency.toUpperCase()) return native;
  const explicit = line.fx_rate == null ? null : Number(line.fx_rate);
  const rate = explicit != null && explicit > 0 ? explicit : rateFor(rates, lineCurrency, documentCurrency);
  if (rate == null || rate <= 0) return null;
  return native * rate;
}

function rateFor(rates: FxRate[], from: string, to: string): number | null {
  const base = from.toUpperCase();
  const quote = to.toUpperCase();
  if (!base || base === quote) return 1;
  const hit = rates.find((item) => item.base_currency === base && item.quote_currency === quote);
  return hit ? num(hit.rate) : null;
}

function ageLabel(createdAt: string | null | undefined): string | undefined {
  if (!createdAt) return undefined;
  const created = new Date(createdAt).getTime();
  if (!Number.isFinite(created)) return undefined;
  const days = Math.max(0, Math.floor((Date.now() - created) / 86_400_000));
  if (days === 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function DocumentPreview({ html }: { html: string }) {
  return (
    <div className="crm-document-sheet" dangerouslySetInnerHTML={{ __html: html }} />
  );
}

export function QuoteDetailPage({ orgId }: { orgId: string }) {
  const { quoteId = "" } = useParams();
  return <CommercialRecordPage orgId={orgId} kind="quote" recordId={quoteId} />;
}

export function OrderDetailPage({ orgId }: { orgId: string }) {
  const { orderId = "" } = useParams();
  return <CommercialRecordPage orgId={orgId} kind="order" recordId={orderId} />;
}

export function InvoiceDetailPage({ orgId }: { orgId: string }) {
  const { invoiceId = "" } = useParams();
  return <CommercialRecordPage orgId={orgId} kind="invoice" recordId={invoiceId} />;
}

function CommercialRecordPage({ orgId, kind, recordId }: { orgId: string; kind: Kind; recordId: string }) {
  const navigate = useNavigate();
  const crmHref = useCrmHref();
  const { show } = useToast();
  const { formatMoney, formatDate, currency: defaultCurrency, quoteApprovalRequired, timezone } = useLocale();
  const { can } = useAccess();
  const canCreateOrder = can("crm.orders", "create");
  const [payingInvoice, setPayingInvoice] = useState(false);
  const [tab, setTab] = useState<Tab>("document");
  const [quote, setQuote] = useState<Quote | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const shownRecord = kind === "quote" ? quote : kind === "order" ? order : invoice;
  const shownNumber = quote?.quote_number ?? order?.order_number ?? invoice?.invoice_number;
  useLoxleyOffer(shownRecord ? { kind, id: shownRecord.id, label: shownNumber || `This ${kind}` } : null);
  const [products, setProducts] = useState<Product[]>([]);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentLink, setSentLink] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("");
  const [expiry, setExpiry] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [fxRates, setFxRates] = useState<FxRate[]>([]);
  const [orderStatus, setOrderStatus] = useState("");
  const [lines, setLines] = useState<QuoteLineItem[]>([]);
  const [changes, setChanges] = useState<RecordChange[]>([]);
  const [versions, setVersions] = useState<QuoteVersionSummary[]>([]);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    setError(null);
    try {
      if (kind === "quote") {
        const data = await crmApi.getQuote(recordId, orgId);
        setQuote(data);
        setNotes(data.notes ?? "");
        setTerms(data.terms ?? "");
        setExpiry(data.expiry_date?.slice(0, 10) ?? "");
        setIssueDate(data.issue_date?.slice(0, 10) ?? data.created_at?.slice(0, 10) ?? "");
        setCurrency(data.currency ?? defaultCurrency);
        setLines(data.line_items?.length ? data.line_items : [emptyLine()]);
        setProducts(await crmApi.listProducts(orgId, undefined, pickerPage()));
        const history = await crmApi.getQuoteHistory(recordId, orgId).catch(() => ({ changes: [] }));
        setChanges(history.changes);
        const versionList = await crmApi.listQuoteVersions(recordId, orgId).catch(() => ({ versions: [] }));
        setVersions(versionList.versions);
      } else if (kind === "order") {
        const data = await crmApi.getOrder(recordId, orgId);
        setOrder(data);
        setNotes(data.notes ?? "");
        setOrderStatus(data.order_status ?? "pending");
        setLines(data.line_items ?? []);
      } else {
        const data = await opsApi.getInvoice(orgId, recordId);
        setInvoice(data);
        setLines(
          (data.lines ?? []).map((line) => ({
            description: line.description ?? "",
            quantity: num(line.quantity, 1),
            unit_price: num(line.unit_price),
            discount_pct: num(line.discount_pct),
            currency: line.currency,
            fx_rate: line.fx_rate == null || line.fx_rate === "" ? null : num(line.fx_rate),
          })),
        );
      }
      try {
        setHtml((await opsApi.previewRecord(orgId, kind, recordId)).html);
      } catch (previewErr) {
        setHtml("");
        setError(previewErr instanceof Error ? previewErr.message : "Preview could not be loaded.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Record could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [kind, orgId, recordId, defaultCurrency]);

  useEffect(() => {
    void load();
  }, [load]);

  const quoteEditable = quote?.quote_status === "draft" || quote?.quote_status === "rejected";
  const orderEditable = order?.order_status === "pending";
  const invoiceEditable = false;
  const recordEditable = kind === "quote" ? quoteEditable : kind === "order" ? orderEditable : invoiceEditable;
  const number = quote?.quote_number ?? order?.order_number ?? invoice?.invoice_number ?? "";
  const status = quote?.quote_status ?? order?.order_status ?? invoice?.status ?? "";
  const companyName = quote?.company_name ?? order?.company_name ?? invoice?.company_name ?? "";
  const companyId = quote?.company_id ?? order?.company_id ?? invoice?.company_id;
  const total = quote?.total ?? order?.total ?? invoice?.total;
  const aging = ageLabel(quote?.created_at ?? order?.created_at ?? invoice?.issued_at);
  const objectLabel = kind === "quote" ? "Quote" : kind === "order" ? "Order" : "Invoice";

  const cataloguePrice = useCallback((line: QuoteLineItem): number => {
    const typed = num(line.unit_price);
    if (typed > 0) return typed;
    const product = products.find((item) => item.id === line.product_id);
    return num(product?.unit_price);
  }, [products]);
  const pricedLine = useCallback((line: QuoteLineItem): QuoteLineItem => (
    { ...line, unit_price: cataloguePrice(line) }
  ), [cataloguePrice]);
  const lineTotal = useMemo(
    () => lines.reduce((sum, line) => sum + (convertedNet(pricedLine(line), currency, fxRates) ?? 0), 0),
    [lines, currency, pricedLine, fxRates],
  );
  const shownTotal = kind === "quote" && quoteEditable ? lineTotal : total;
  const rateMissing = lines.some((line) => convertedNet(line, currency, fxRates) == null);
  // "EUR to USD" for each line currency with no rate, so the tip names what is missing.
  const missingPairs = useMemo(
    () => [
      ...new Set(
        lines
          .filter((line) => convertedNet(line, currency, fxRates) == null)
          .map((line) => `${(line.currency || currency).toUpperCase()} to ${currency.toUpperCase()}`),
      ),
    ],
    [lines, currency, fxRates],
  );
  const [fxPullEnabled, setFxPullEnabled] = useState(false);
  const canConfigureRates = can("crm.companies", "configure");

  useEffect(() => {
    void opsApi
      .fxRates(orgId)
      .then((row) => {
        setFxRates(row.rates);
        setFxPullEnabled(Boolean(row.enabled));
      })
      .catch(() => setFxRates([]));
  }, [orgId]);

  useEffect(() => {
    if (kind !== "quote" || products.length === 0) return;
    setLines((current) => {
      let changed = false;
      const next = current.map((line) => {
        if (!line.product_id || num(line.unit_price) > 0) return line;
        const product = products.find((item) => item.id === line.product_id);
        const price = num(product?.unit_price);
        if (!product || price <= 0) return line;
        changed = true;
        return {
          ...line,
          unit_price: price,
          description: (line.description || "").trim() || product.name || line.description,
          currency: line.currency || product.currency || "",
        };
      });
      return changed ? next : current;
    });
  }, [kind, products]);

  async function confirmPaid(body: { payment_reference: string; payment_date: string }) {
    if (!invoice) return;
    await opsApi.payInvoice(orgId, invoice.id, body);
    await load();
    show("Saved!", "success");
  }

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      const result = await action();
      if (result && typeof result === "object" && "accept_token" in result && typeof result.accept_token === "string") {
        setSentLink(`${window.location.origin}/quote-accept/${result.accept_token}`);
      }
      if (result && typeof result === "object" && "order_number" in result && "id" in result && kind === "quote") {
        show("Saved!", "success");
        navigate(crmHref(`/orders/${String((result as { id: string }).id)}`));
        return;
      }
      show("Saved!", "success");
      await load();
    } catch (err) {
      setError(reportSaveFailure(show, err, "Update failed."));
    }
  }

  async function exportPdf() {
    if (exporting) return;
    setExporting(true);
    setError(null);
    try {
      const idempotencyKey = `${kind}-${recordId}-${crypto.randomUUID()}`;
      let job = await opsApi.createExportJob(orgId, kind, recordId, idempotencyKey);
      const deadline = Date.now() + 60_000;
      while (job.status === "queued" || job.status === "processing") {
        if (Date.now() > deadline) throw new Error("Export is taking longer than expected. Try again shortly.");
        await new Promise((resolve) => setTimeout(resolve, 2000));
        job = await opsApi.getExportJob(orgId, job.id);
      }
      if (job.status === "failed") throw new Error(job.error ?? "Export failed.");
      await opsApi.downloadExportJob(orgId, job.id, job.filename ?? `${kind}.pdf`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  }

  async function saveQuote() {
    if (!quote || !quoteEditable) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await crmApi.updateQuote(
        quote.id,
        {
          notes: notes || null,
          terms: terms || null,
          expiry_date: expiry || null,
          issue_date: issueDate || null,
          currency,
          line_items: lines
            .filter((line) => (line.description || "").trim() || num(line.unit_price) > 0 || line.product_id)
            .map((line, index) => ({
              product_id: line.product_id || null,
              description: line.description || null,
              quantity: num(line.quantity, 1),
              unit_price: num(line.unit_price),
              discount_pct: num(line.discount_pct),
              currency: (line.currency || currency).toUpperCase(),
              fx_rate: line.fx_rate,
              sort_order: index,
            })),
        },
        orgId,
      );
      setQuote(saved);
      setLines(saved.line_items?.length ? saved.line_items : [emptyLine()]);
      setHtml((await opsApi.previewRecord(orgId, kind, recordId)).html);
      const versionList = await crmApi.listQuoteVersions(recordId, orgId).catch(() => ({ versions: [] }));
      setVersions(versionList.versions);
      setTab("document");
      show("Saved!", "success");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Quote could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  async function saveOrder() {
    if (!order || !orderEditable) return;
    setSaving(true);
    setError(null);
    try {
      await crmApi.updateOrder(order.id, { notes: notes || null, order_status: orderStatus }, orgId);
      await load();
      setTab("document");
      show("Saved!", "success");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Order could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Card>Loading document…</Card>;
  if (error && !quote && !order && !invoice) return <Card className="crm-error">{error}</Card>;

  const quoteFlow = kind === "quote" && quote
    ? quoteWorkflow(
        quote.quote_status,
        quote.quote_approval_required ?? quoteApprovalRequired,
        Boolean(quote.order_id),
        quote.total ?? null,
      )
    : null;

  return (
    <RecordWorkspace
      kind={kind}
      objectLabel={objectLabel}
      title={number || objectLabel}
      subtitle={companyName}
      backTo={crmHref(`/${kind}s`)}
      backLabel={`← Back to ${objectLabel}s`}
      highlights={[
        {
          label: "Status",
          value: (
            <StatusBadge
              status={status}
              options={
                kind === "quote"
                  ? QUOTE_STATUS_OPTIONS
                  : kind === "order"
                    ? ORDER_STATUS_OPTIONS
                    : INVOICE_STATUS_OPTIONS
              }
            />
          ),
        },
        {
          label: "Company",
          value: companyId ? <Link to={crmHref(`/companies/${companyId}`)}>{companyName}</Link> : companyName,
        },
        ...(kind === "quote" && quote
          ? [{
              label: "Salesperson",
              value: (
                <RecordOwner
                  orgId={orgId}
                  kind="quotes"
                  recordId={quote.id}
                  ownerUserId={quote.owner_user_id}
                  ownerName={quote.owner_name}
                  canReassign={quote.can_reassign_owner}
                  onChanged={(owner) => setQuote((prev) => (prev ? { ...prev, ...owner } : prev))}
                />
              ),
            }]
          : []),
        ...(kind === "order" && order
          ? [{
              label: "Salesperson",
              value: (
                <RecordOwner
                  orgId={orgId}
                  kind="orders"
                  recordId={order.id}
                  ownerUserId={order.owner_user_id}
                  ownerName={order.owner_name}
                  canReassign={order.can_reassign_owner}
                  onChanged={(owner) => setOrder((prev) => (prev ? { ...prev, ...owner } : prev))}
                />
              ),
            }]
          : []),
        ...(kind !== "quote" && number ? [{ label: `${objectLabel} number`, value: number }] : []),
        ...(kind === "quote" && quote?.order_id
          ? [{ label: "Order", value: <Link to={crmHref(`/orders/${quote.order_id}`)}>{quote.order_number || "Open order"}</Link> }]
          : []),
        ...(kind === "quote" && quote?.invoice_id
          ? [{ label: "Invoice", value: <Link to={crmHref(`/invoices/${quote.invoice_id}`)}>{quote.invoice_number || "Open invoice"}</Link> }]
          : []),
        ...(kind === "order" && order?.quote_id
          ? [{ label: "Quote", value: <Link to={crmHref(`/quotes/${order.quote_id}`)}>{order.quote_number || "Open quote"}</Link> }]
          : []),
        ...(kind === "order" && order?.invoice_id
          ? [{ label: "Invoice", value: <Link to={crmHref(`/invoices/${order.invoice_id}`)}>{order.invoice_number || "Open invoice"}</Link> }]
          : []),
        ...(kind === "invoice" && invoice?.order_id
          ? [{ label: "Order", value: <Link to={crmHref(`/orders/${invoice.order_id}`)}>{invoice.order_number || "Open order"}</Link> }]
          : []),
        ...(kind === "invoice" && invoice?.quote_id
          ? [{ label: "Quote", value: <Link to={crmHref(`/quotes/${invoice.quote_id}`)}>{invoice.quote_number || "Open quote"}</Link> }]
          : []),
        { label: "Deal value", value: formatMoney(shownTotal, quote?.currency ?? order?.currency ?? invoice?.currency) },
        ...(aging ? [{ label: "Aging", value: <span className="crm-badge crm-badge--aging">{aging}</span> }] : []),
        ...(kind === "quote" && quote?.quote_version
          ? [{ label: "Version", value: String(quote.quote_version).padStart(2, "0") }]
          : []),
        ...(kind === "quote" && quote?.opportunity_id
          ? [{
              label: "Opportunity",
              value: (
                <Link to={crmHref(`/opportunities/${quote.opportunity_id}`)}>
                  {quote.opportunity_code || quote.opportunity_title || "Open opportunity"}
                </Link>
              ),
            }]
          : []),
        ...(kind === "quote" && quote?.source_lead_id
          ? [{
              label: "Lead",
              value: (
                <Link to={crmHref(`/leads/${quote.source_lead_id}`)}>
                  {quote.source_lead_code || "Open lead"}
                </Link>
              ),
            }]
          : []),
        ...(kind === "invoice" && invoice
          ? [
              {
                label: "Salesperson",
                hint: "The salesperson on the order. If the lead and the order name different people, this is the person on the order.",
                value: invoice.sales_person_name || "—",
              },
              {
                label: "Created by",
                hint: "The person who generated this invoice.",
                value: invoice.created_by_name || "—",
              },
            ]
          : []),
      ]}
      actions={
        <ResponsiveActions
          primary={
            <>
              {quoteFlow?.canSubmit && quote && (
                <Button
                  title="Send this quote to an approver. Required before you can send it to the client."
                  onClick={() => run(() => crmApi.submitQuote(quote.id, orgId))}
                >
                  Submit for approval
                </Button>
              )}
              {quoteFlow?.canApprove && quote && (
                <Button
                  title="Approve this quote so it can be sent to the client."
                  onClick={() => run(() => crmApi.approveQuote(quote.id, undefined, orgId))}
                >
                  Approve
                </Button>
              )}
              {quoteFlow?.canSend && quote && (
                <Button
                  title="Send this quote to the client and create a customer accept link."
                  onClick={() => run(() => crmApi.sendQuote(quote.id, orgId))}
                >
                  Send to client
                </Button>
              )}
              {quoteFlow?.canAccept && quote && (
                <Button
                  variant="secondary"
                  title="Record that the client accepted — for example after a call — without using the online accept link."
                  onClick={() => run(() => crmApi.acceptQuote(quote.id, orgId))}
                >
                  Mark accepted
                </Button>
              )}
              {quoteFlow?.canGenerateOrder && canCreateOrder && quote && (
                <Button
                  title="Create an order from this accepted quote."
                  onClick={() => run(() => crmApi.generateOrder(quote.id, orgId))}
                >
                  Generate order
                </Button>
              )}
              {quoteFlow?.canRevise && quote && (
                <Button
                  variant="secondary"
                  title="Start a new draft revision based on this quote."
                  onClick={() => run(() => crmApi.reviseQuote(quote.id, orgId))}
                >
                  New revision
                </Button>
              )}
              {kind === "order" && order?.order_status === "fulfilled" && !order.invoice_id && (
                <Button
                  title="Create an invoice from this fulfilled order, or open the one already created."
                  onClick={async () => {
                    setError(null);
                    try {
                      const created = await opsApi.createInvoice(orgId, order.id);
                      show("Invoice ready", "success");
                      navigate(crmHref(`/invoices/${created.id}`));
                    } catch (err) {
                      setError(reportSaveFailure(show, err, "Invoice could not be generated."));
                    }
                  }}
                >
                  Generate invoice
                </Button>
              )}
              {kind === "invoice" && invoice?.status === "issued" && (
                <Button title={ACTION_HINTS.mark_paid} onClick={() => setPayingInvoice(true)}>
                  Mark paid
                </Button>
              )}
              {kind === "invoice" && invoice?.status === "paid" && (
                <Button
                  variant="secondary"
                  title={ACTION_HINTS.undo_paid}
                  onClick={() => run(() => opsApi.unpayInvoice(orgId, invoice.id))}
                >
                  Undo paid
                </Button>
              )}
            </>
          }
          more={
            <>
              <Button
                variant="secondary"
                disabled={!html}
                title="Open the browser print dialog for this document preview."
                onClick={() => printDocumentHtml(html, `${objectLabel} ${number}`.trim())}
              >
                Print preview
              </Button>
              <Button
                variant="ghost"
                title="Generate and download a PDF of this document."
                disabled={exporting}
                onClick={() => exportPdf()}
              >
                {exporting ? <><Spinner size={14} /> Preparing PDF...</> : "Download PDF"}
              </Button>
              {quoteFlow?.canApprove && quote && (
                <Button
                  variant="ghost"
                  title="Reject this quote and return it for revision."
                  onClick={() => run(() => crmApi.rejectQuote(quote.id, "Needs revision", orgId))}
                >
                  Reject
                </Button>
              )}
              {quote && quote.quote_status === "accepted" && quote.order_id && (
                <Button
                  variant="secondary"
                  disabled
                  title={`Order ${quote.order_number ?? ""} has already been generated from this quote.`}
                >
                  Order generated
                </Button>
              )}
              {kind === "order" && order?.order_status === "fulfilled" && order.invoice_id && (
                <Button
                  variant="secondary"
                  disabled
                  title={`Invoice ${order.invoice_number ?? ""} has already been generated from this order.`}
                >
                  Invoiced
                </Button>
              )}
              {!(kind === "invoice" && invoice?.status === "paid") && (
                <DeleteRecordButton
                  capability={kind === "quote" ? "crm.quotes" : kind === "order" ? "crm.orders" : "crm.invoices"}
                  recordLabel={number || objectLabel}
                  onDeleted={async () => {
                    if (kind === "quote") await crmApi.deleteQuote(recordId, orgId);
                    else if (kind === "order") await crmApi.deleteOrder(recordId, orgId);
                    else await opsApi.deleteInvoice(orgId, recordId);
                    navigate(crmHref(`/${kind}s`));
                  }}
                />
              )}
            </>
          }
        />
      }
      tabs={[
        { key: "document", label: "Document" },
        { key: "edit", label: recordEditable ? "Edit" : "View" },
        ...(kind === "quote" ? [{ key: "history", label: "Versions" }] : []),
      ]}
      activeTab={tab}
      onTabChange={(key) => setTab(key as Tab)}
    >
      {error && <p className="crm-error">{error}</p>}
      {payingInvoice && invoice ? (
        <MarkPaidDialog
          invoiceNumber={invoice.invoice_number}
          today={todayInZone(timezone)}
          onClose={() => setPayingInvoice(false)}
          onConfirm={confirmPaid}
        />
      ) : null}
      {sentLink && (
        <Card>
          <p>Customer accept link</p>
          <code className="settings-setup-link">{sentLink}</code>
        </Card>
      )}

      {tab === "document" && (
        <Stack gap={3}>
          <p className="crm-help">This is the document the client sees. Save edits before sending so the preview matches the file you send.</p>
          {html ? <DocumentPreview html={html} /> : <p className="crm-muted">No preview available.</p>}
        </Stack>
      )}

      {tab === "edit" && kind === "quote" && quote && (
        <Stack gap={4}>
          {!quoteEditable && (
            <p className="crm-help">This quote is {quote.quote_status}. Line items and terms can only be changed while it is a draft or after it is rejected.</p>
          )}
          <DetailSection title="Quote details">
            <FieldItem label="Company" value={quote.company_id ? <Link to={crmHref(`/companies/${quote.company_id}`)}>{companyName || "Open company"}</Link> : companyName} />
            <FieldItem label="Quote number" value={quote.quote_number} />
            <FieldItem label="Version" value={String(quote.quote_version ?? 1).padStart(2, "0")} />
            <FieldItem label="Status" value={quote.quote_status} />
            <FieldItem label="Total" value={formatMoney(shownTotal, currency)} />
          </DetailSection>
          <div className="crm-field-grid">
            <Input label="Issue date" tooltip={FIELD_HINTS.quote_issue_date} type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} disabled={!quoteEditable} />
            <Input label="Expiry date" tooltip={FIELD_HINTS.quote_expiry_date} type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} disabled={!quoteEditable} />
            <Select
              label="Currency"
              tooltip={FIELD_HINTS.quote_currency}
              value={currency}
              disabled={!quoteEditable}
              onChange={(event) => {
                const next = event.target.value.toUpperCase();
                setCurrency(next);
                setLines((current) => current.map((row) => {
                  const lineCurrency = (row.currency || next).toUpperCase();
                  return { ...row, fx_rate: rateFor(fxRates, lineCurrency, next) };
                }));
              }}
            >
              {CURRENCY_CODES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </Select>
          </div>
          <label className="crm-field">
            <span>Notes</span>
            <textarea className="crm-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!quoteEditable} rows={3} />
          </label>
          <label className="crm-field">
            <span>Terms</span>
            <textarea className="crm-textarea" value={terms} onChange={(e) => setTerms(e.target.value)} disabled={!quoteEditable} rows={3} />
          </label>
          <DetailSection title="Line items">
            <div className="crm-line-editor">
              <table className="crm-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Unit price</th>
                    <th>Currency</th>
                    <th>Rate</th>
                    <th>Discount %</th>
                    <th>Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => {
                    const lineCurrency = (line.currency || currency).toUpperCase();
                    const unitPrice = cataloguePrice(line);
                    const converted = convertedNet({ ...line, unit_price: unitPrice }, currency);
                    const sameCurrency = lineCurrency === currency.toUpperCase();
                    return (
                      <tr key={line.id ?? index}>
                        <td>
                          <Select
                            aria-label="Product"
                            value={line.product_id ?? ""}
                            disabled={!quoteEditable}
                            onChange={(event) => {
                              const product = products.find((item) => item.id === event.target.value);
                              setLines((current) =>
                                current.map((row, rowIndex) =>
                                  rowIndex === index
                                    ? {
                                        ...row,
                                        product_id: product?.id ?? null,
                                        description: product?.name ?? row.description,
                                        unit_price: num(product?.unit_price) > 0 ? num(product?.unit_price) : 0,
                                        currency: (product?.currency || row.currency || currency).toUpperCase(),
                                        fx_rate: rateFor(fxRates, product?.currency || row.currency || currency, currency),
                                      }
                                    : row,
                                ),
                              );
                            }}
                          >
                            <option value="">Custom line</option>
                            {products.map((product) => (
                              <option key={product.id} value={product.id}>{product.name}</option>
                            ))}
                          </Select>
                          {line.product_id ? (
                            <Cluster gap={2}>
                              <Link to={crmHref(`/products?q=${encodeURIComponent(products.find((item) => item.id === line.product_id)?.sku || line.description || "")}`)}>
                                View product
                              </Link>
                              {line.agreement_id ? (
                                <Link to={crmHref(`/agreements/${line.agreement_id}`)}>
                                  Agreement {line.agreement_number ?? line.agreement_line_code}
                                </Link>
                              ) : null}
                            </Cluster>
                          ) : null}
                        </td>
                        <td>
                          <Input
                            value={line.description ?? ""}
                            disabled={!quoteEditable}
                            onChange={(event) =>
                              setLines((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, description: event.target.value } : row)))
                            }
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            value={String(line.quantity ?? 1)}
                            disabled={!quoteEditable}
                            onChange={(event) =>
                              setLines((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, quantity: num(event.target.value, 1) } : row)))
                            }
                          />
                        </td>
                        <td>
                          <Input
                            type="number"
                            value={String(line.unit_price ?? unitPrice ?? 0)}
                            disabled={!quoteEditable}
                            onChange={(event) =>
                              setLines((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, unit_price: num(event.target.value) } : row)))
                            }
                          />
                        </td>
                        <td>
                          <Select
                            aria-label="Line currency"
                            value={lineCurrency}
                            disabled={!quoteEditable}
                            onChange={(event) => {
                              const next = event.target.value.toUpperCase();
                              setLines((current) => current.map((row, rowIndex) => (
                                rowIndex === index ? { ...row, currency: next, fx_rate: rateFor(fxRates, next, currency) } : row
                              )));
                            }}
                          >
                            {CURRENCY_CODES.map((code) => (
                              <option key={code} value={code}>{code}</option>
                            ))}
                          </Select>
                        </td>
                        <td>
                          {sameCurrency ? (
                            "1"
                          ) : (
                            <Input
                              aria-label="Exchange rate"
                              type="number"
                              value={line.fx_rate == null ? "" : String(line.fx_rate)}
                              disabled={!quoteEditable}
                              onChange={(event) => {
                                const next = event.target.value.trim();
                                setLines((current) => current.map((row, rowIndex) => (
                                  rowIndex === index ? { ...row, fx_rate: next === "" ? null : num(next) } : row
                                )));
                              }}
                            />
                          )}
                        </td>
                        <td>
                          <Input
                            type="number"
                            aria-label="Discount %"
                            value={String(line.discount_pct ?? 0)}
                            disabled={!quoteEditable}
                            onChange={(event) =>
                              setLines((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, discount_pct: num(event.target.value) } : row)))
                            }
                          />
                        </td>
                        <td>{converted == null ? "Set a rate" : formatMoney(converted, currency)}</td>
                        <td>
                          {quoteEditable && (
                            <Button variant="ghost" size="sm" onClick={() => setLines((current) => current.filter((_, rowIndex) => rowIndex !== index))}>
                              Remove
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {quoteEditable && (
                <Button variant="secondary" onClick={() => setLines((current) => [...current, emptyLine()])}>Add line</Button>
              )}
              <p className="crm-help">
                Line total {formatMoney(lineTotal, currency)}
                {rateMissing ? " Lines without a rate are not included." : ""}
              </p>
              {rateMissing && (
                <p className="crm-help" role="note" data-testid="fx-rate-tip">
                  No exchange rate for {missingPairs.join(", ")}. Type the rate on the line.{" "}
                  {fxPullEnabled
                    ? "Pulled rates are on, but the exchange rate address returned no rate for this pair."
                    : "Or switch on Pull exchange rates every day so rates fill in automatically."}{" "}
                  {canConfigureRates ? (
                    <Link to={`${crmHref("/settings")}?tab=locale`}>
                      {fxPullEnabled ? "Check the exchange rate address" : "Open exchange rate settings"}
                    </Link>
                  ) : (
                    `Ask your organization administrator to ${
                      fxPullEnabled ? "check the Exchange rate address" : "switch it on"
                    } in Settings → Territories & Locale.`
                  )}
                </p>
              )}
              {(quote.fx_changes ?? []).length > 0 && (
                <Stack gap={1}>
                  <p className="crm-help">Rate changes</p>
                  {quote.fx_changes?.map((change, index) => (
                    <p key={`${change.created_at}-${index}`} className="crm-help">
                      {change.base_currency} to {change.quote_currency}: {change.old_rate ?? "none"} → {change.new_rate}
                      {change.actor_name ? ` by ${change.actor_name}` : ""}
                      {change.created_at ? ` on ${formatDate(change.created_at)}` : ""}
                    </p>
                  ))}
                </Stack>
              )}
            </div>
          </DetailSection>
          {quoteEditable && (
            <div className="crm-form-actions">
              <Button onClick={() => saveQuote()} disabled={saving}>{saving ? "Saving…" : "Save and refresh preview"}</Button>
            </div>
          )}
        </Stack>
      )}

      {tab === "edit" && kind === "order" && order && (
        <Stack gap={4}>
          {!orderEditable && (
            <p className="crm-help">This order is {order.order_status}. Notes and status can only be changed while it is pending.</p>
          )}
          <DetailSection title="Order details">
            <FieldItem label="Company" value={order.company_id ? <Link to={crmHref(`/companies/${order.company_id}`)}>{companyName}</Link> : companyName} />
            <FieldItem label="Order number" value={order.order_number} />
            {order.invoice_id && (
              <FieldItem label="Invoice" value={<Link to={crmHref(`/invoices/${order.invoice_id}`)}>{order.invoice_number || "Open invoice"}</Link>} />
            )}
            {order.quote_id && (
              <FieldItem
                label="Source quote"
                value={
                  <Link to={crmHref(`/quotes/${order.quote_id}`)}>
                    {order.quote_number || "Open quote"}
                  </Link>
                }
              />
            )}
            <FieldItem label="Total" value={formatMoney(order.total, order.currency)} />
          </DetailSection>
          {order.contract_number && (
            <DetailSection title="Contract terms">
              <FieldItem
                label="Contract"
                value={
                  order.contract_agreement_id
                    ? <Link to={crmHref(`/agreements/${order.contract_agreement_id}`)}>{order.contract_number}</Link>
                    : order.contract_number
                }
              />
              <FieldItem label="Delivery terms" value={order.contract_delivery_terms || "-"} />
              <FieldItem label="Payment terms" value={order.contract_payment_terms || "-"} />
              <FieldItem
                label="Lead time"
                value={order.contract_lead_time_days != null ? `${order.contract_lead_time_days} days` : "-"}
              />
            </DetailSection>
          )}
          <Select
            label="Status"
            tooltip={FIELD_HINTS.order_status}
            value={orderStatus}
            onChange={(event) => setOrderStatus(event.target.value)}
            aria-label="Order status"
            disabled={!orderEditable}
          >
            <option value="pending" title={ORDER_STATUS_OPTIONS[0].meaning}>
              Pending
            </option>
            <option value="confirmed" title={ORDER_STATUS_OPTIONS[1].meaning}>
              Confirmed
            </option>
            <option value="fulfilled" title={ORDER_STATUS_OPTIONS[2].meaning}>
              Fulfilled
            </option>
            <option value="cancelled" title={ORDER_STATUS_OPTIONS[3].meaning}>
              Cancelled
            </option>
          </Select>
          <label className="crm-field">
            <LabelWithHint label="Notes" tooltip={FIELD_HINTS.order_notes} />
            <textarea className="crm-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={!orderEditable} />
          </label>
          <DetailSection title="Line items">
            <table className="crm-table">
              <thead>
                <tr><th>Description</th><th>Qty</th><th>Unit price</th><th>Currency</th><th>Rate</th><th>Discount %</th><th>Amount</th></tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={line.id ?? index}>
                    <td>{line.description}</td>
                    <td>{line.quantity}</td>
                    <td>{formatMoney(line.unit_price, line.currency || order.currency)}</td>
                    <td>{line.currency || order.currency || "—"}</td>
                    <td>{rateLabel(line, order.currency || currency)}</td>
                    <td>{line.discount_pct ?? 0}</td>
                    <td>
                      {convertedNet(line, order.currency || currency) == null
                        ? "—"
                        : formatMoney(convertedNet(line, order.currency || currency), order.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="crm-help">Order lines are copied from the accepted quote and cannot be rewritten here.</p>
          </DetailSection>
          {orderEditable && (
            <div className="crm-form-actions">
              <Button onClick={() => saveOrder()} disabled={saving}>{saving ? "Saving…" : "Save and refresh preview"}</Button>
            </div>
          )}
        </Stack>
      )}

      {tab === "edit" && kind === "invoice" && invoice && (
        <Stack gap={4}>
          <DetailSection title="Invoice details">
            <FieldItem label="Company" value={invoice.company_id ? <Link to={crmHref(`/companies/${invoice.company_id}`)}>{companyName}</Link> : companyName} />
            <FieldItem label="Invoice number" value={invoice.invoice_number} />
            <FieldItem label="Order" value={<Link to={crmHref(`/orders/${invoice.order_id}`)}>{invoice.order_number ?? "Open order"}</Link>} />
            {invoice.quote_id && (
              <FieldItem label="Quote" value={<Link to={crmHref(`/quotes/${invoice.quote_id}`)}>{invoice.quote_number || "Open quote"}</Link>} />
            )}
            <FieldItem label="Status" value={invoice.status} />
            <FieldItem label="Salesperson" value={invoice.sales_person_name || "—"} />
            <FieldItem label="Created by" value={invoice.created_by_name || "—"} />
            <FieldItem label="Total" value={formatMoney(invoice.total, invoice.currency)} />
            <FieldItem label="Created" value={invoice.created_at ? formatDate(invoice.created_at) : "—"} />
            <FieldItem label="Issued" value={invoice.issued_at ? formatDate(invoice.issued_at) : "—"} />
            <FieldItem label="Paid" value={invoice.paid_at ? formatDate(invoice.paid_at) : "—"} />
            <FieldItem label="Payment reference" value={invoice.payment_reference || "—"} />
            <FieldItem label="Payment date" value={invoice.payment_date ? formatDate(invoice.payment_date) : "—"} />
          </DetailSection>
          <DetailSection title="Line items">
            <table className="crm-table">
              <thead>
                <tr><th>Description</th><th>Qty</th><th>Unit price</th><th>Currency</th><th>Rate</th><th>Discount %</th><th>Amount</th></tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index}>
                    <td>{line.description}</td>
                    <td>{line.quantity}</td>
                    <td>{formatMoney(line.unit_price, line.currency || invoice.currency)}</td>
                    <td>{line.currency || invoice.currency || "—"}</td>
                    <td>{rateLabel(line, invoice.currency || currency)}</td>
                    <td>{line.discount_pct ?? 0}</td>
                    <td>
                      {convertedNet(line, invoice.currency || currency) == null
                        ? "—"
                        : formatMoney(convertedNet(line, invoice.currency || currency), invoice.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="crm-help">Invoice lines are frozen from the order. Change the quote while it is still a draft if the commercial terms are wrong.</p>
          </DetailSection>
        </Stack>
      )}

      {tab === "history" && kind === "quote" && quote && (
        <Stack gap={4}>
          <QuoteVersionsPanel
            orgId={orgId}
            quoteId={recordId}
            versions={versions}
            currentVersionNumber={quote.quote_version ?? null}
            onActivated={load}
          />
          <ChangeHistoryPanel
            title="Field-level change log"
            changes={changes}
            emptyTitle="No changes yet"
            emptyDescription="Edits and status changes are logged here with the old and new values."
          />
        </Stack>
      )}
    </RecordWorkspace>
  );
}
